/**
 * A path router in fifty lines.
 *
 * Path-based rather than the original design's `#/route`, because landing and gtm ARE the SEO asset
 * and a hash route is one URL to a crawler. The console gets the same thing for a different
 * reason: it is behind a key so it needs no crawler, but it does need deep links, a back
 * button, and a shareable approval — none of which `useState<View>` can give you.
 *
 * No router library: what is needed here is match, navigate, and intercept same-origin clicks.
 * Everything past that (loaders, nested layouts, transitions) is machinery these three pages
 * would carry and not use.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";

interface RouteState {
  path: string;
  navigate: (to: string) => void;
}

const Ctx = createContext<RouteState>({ path: "/", navigate: () => {} });

/** The current path, and a way to change it. */
export const useRoute = () => useContext(Ctx);

export function Router({ base = "", children }: { base?: string; children: ReactNode }) {
  const [path, setPath] = useState(() => strip(location.pathname, base));

  const navigate = useCallback(
    (to: string) => {
      history.pushState(null, "", base + to);
      setPath(to);
      window.scrollTo(0, 0);
    },
    [base],
  );

  useEffect(() => {
    const onPop = () => setPath(strip(location.pathname, base));
    addEventListener("popstate", onPop);
    return () => removeEventListener("popstate", onPop);
  }, [base]);

  // One delegated listener rather than a <Link> component every call site has to remember to
  // use. A plain <a href> that full-page-reloads is not broken, just slow — but it also loses
  // the scroll position and re-runs every fetch, and the failure is invisible in review.
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey) return;
      const anchor = (e.target as HTMLElement | null)?.closest?.("a");
      if (!anchor) return;
      const href = anchor.getAttribute("href") ?? "";
      // Leave external links, downloads, new tabs and in-page anchors entirely alone.
      if (!href.startsWith("/") || anchor.target || anchor.hasAttribute("download")) return;
      if (anchor.getAttribute("rel")?.includes("external")) return;
      e.preventDefault();
      navigate(strip(href, base));
    };
    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, [base, navigate]);

  const value = useMemo(() => ({ path, navigate }), [path, navigate]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

function strip(pathname: string, base: string): string {
  const p = base && pathname.startsWith(base) ? pathname.slice(base.length) : pathname;
  return p || "/";
}

/**
 * Match a path against a table of patterns. `:param` captures one segment.
 *
 * Ordered: the first match wins, so a literal `/directory` must be listed before
 * `/directory/:host` — the same registration-order trap that once made
 * `/directory/{host}.json` unreachable on the server side, answered there by ordering and
 * here by the same discipline.
 */
export function match(
  path: string,
  routes: Array<[pattern: string, render: (params: Record<string, string>) => ReactNode]>,
): ReactNode | null {
  const parts = path.split("/").filter(Boolean);
  for (const [pattern, render] of routes) {
    const want = pattern.split("/").filter(Boolean);
    if (want.length !== parts.length) continue;
    const params: Record<string, string> = {};
    let ok = true;
    for (let i = 0; i < want.length; i++) {
      if (want[i].startsWith(":")) params[want[i].slice(1)] = decodeURIComponent(parts[i]);
      else if (want[i] !== parts[i]) {
        ok = false;
        break;
      }
    }
    if (ok) return render(params);
  }
  return null;
}
